import React from 'react';
import { Skeleton } from './Skeleton';

/**
 * Skeletons das telas principais — versão v2 com shimmer real.
 *
 * Versão anterior usava `animate-pulse` (Tailwind) que só faz opacity flicker.
 * Agora usa o componente `<Skeleton>` que tem gradient varrendo via keyframe
 * `sk-shimmer` (definido em index.css).
 *
 * Cada skeleton imita o esqueleto da página correspondente pra dar continuidade
 * visual durante o load (skeleton bones casam com o final layout).
 */

const ScreenShell = ({ children }: { children: React.ReactNode }) => (
  <div className="relative min-h-full w-full overflow-hidden bg-[#030308] px-4 pb-8 pt-14 sm:px-6">
    <div className="pointer-events-none absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-pink-500/8 via-blue-500/4 to-transparent" />
    <div className="relative z-10">{children}</div>
  </div>
);

export const HomeSkeleton: React.FC = () => (
  <ScreenShell>
    {/* Header: cidade + coin badge */}
    <div className="mb-8 flex items-center justify-between">
      <Skeleton height={44} width={160} borderRadius={12} />
      <Skeleton height={36} width={80} borderRadius={999} />
    </div>
    {/* Credential card */}
    <Skeleton height={220} borderRadius={30} className="mb-6" />
    {/* Section title + subtitle */}
    <Skeleton height={32} width={176} className="mb-3" />
    <Skeleton height={16} width="60%" className="mb-6" />
    {/* CTA bar */}
    <Skeleton height={56} borderRadius={16} className="mb-8" />
    {/* Mission/promo card */}
    <Skeleton height={112} borderRadius={26} className="mb-6" />
    {/* Partners scroll */}
    <div className="mb-10 flex gap-3 overflow-hidden">
      <Skeleton height={192} width={156} borderRadius={26} style={{ flexShrink: 0 }} />
      <Skeleton height={192} width={156} borderRadius={26} style={{ flexShrink: 0 }} />
      <Skeleton height={192} width={156} borderRadius={26} style={{ flexShrink: 0 }} />
    </div>
  </ScreenShell>
);

export const ExploreSkeleton: React.FC = () => (
  <ScreenShell>
    <Skeleton height={40} width={144} className="mb-4" />
    <Skeleton height={56} borderRadius={20} className="mb-4" />
    {/* Category chips */}
    <div className="mb-6 flex gap-2 overflow-hidden">
      <Skeleton height={40} width={96} borderRadius={999} />
      <Skeleton height={40} width={80} borderRadius={999} />
      <Skeleton height={40} width={112} borderRadius={999} />
      <Skeleton height={40} width={88} borderRadius={999} />
    </div>
    {/* Featured banner */}
    <Skeleton height={300} borderRadius={28} className="mb-6" />
    {/* Grid */}
    <div className="grid grid-cols-2 gap-3 sm:gap-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} height={208} borderRadius={26} />
      ))}
    </div>
  </ScreenShell>
);

export const FavoritesSkeleton: React.FC = () => (
  <ScreenShell>
    <Skeleton height={40} width={128} className="mb-2" />
    <Skeleton height={16} width={192} className="mb-6" />
    <div className="grid grid-cols-2 gap-3 sm:gap-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} height={208} borderRadius={26} />
      ))}
    </div>
  </ScreenShell>
);

export const ProfileSkeleton: React.FC = () => (
  <ScreenShell>
    {/* Profile header + credential */}
    <Skeleton height={280} borderRadius={34} className="mb-6" />
    {/* Stats row */}
    <div className="mb-6 grid grid-cols-3 gap-2">
      <Skeleton height={64} borderRadius={16} />
      <Skeleton height={64} borderRadius={16} />
      <Skeleton height={64} borderRadius={16} />
    </div>
    {/* My economy */}
    <Skeleton height={96} borderRadius={24} className="mb-3" />
    {/* Sixy daily quote */}
    <Skeleton height={76} borderRadius={16} className="mb-3" />
    {/* Achievements header */}
    <Skeleton height={120} borderRadius={16} className="mb-3" />
    {/* Achievement grid */}
    <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 mb-6">
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} height={176} borderRadius={12} />
      ))}
    </div>
  </ScreenShell>
);

export const AdminSkeleton: React.FC = () => (
  <ScreenShell>
    <div className="mb-6 flex items-center justify-between">
      <Skeleton height={40} width={176} />
      <Skeleton height={40} width={40} borderRadius={999} />
    </div>
    <div className="grid grid-cols-2 gap-3 sm:gap-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} height={128} borderRadius={28} />
      ))}
    </div>
  </ScreenShell>
);

export const ScannerSkeleton: React.FC = () => (
  <div className="fixed inset-0 z-[70] flex items-center justify-center bg-[#030308]/94 px-6">
    <div className="w-full max-w-sm rounded-[28px] border border-white/10 bg-[#0f0f16] p-6 shadow-[0_24px_60px_rgba(0,0,0,0.32)]">
      <Skeleton height={32} width={128} className="mb-4" />
      <Skeleton height={288} borderRadius={24} />
      <Skeleton height={48} borderRadius={16} className="mt-5" />
    </div>
  </div>
);
